import { useEffect, useRef } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import Layout from './containers/Layout/Layout';
import Login from './containers/Login/Login';
import LandingPage from './containers/LandingPage/LandingPage';
import Dashboard from './containers/Dashboard/Dashboard';
import HealthSystemwiseSummary from './containers/HealthSystemwiseSummary/HealthSystemwiseSummary';
import NotFoundPage from './containers/NotFoundPage/NotFoundPage';
import Unauthorized from './containers/Unauthorized/Unauthorized';
import { ALL_ROUTES } from './constants';
import { useAuth } from './contexts/AuthContext';

/**
 * Route table.
 *
 * "Signed in" means holding a B2C token, which is what DataNovaProxy validates
 * on every call. `AuthContext` owns that state, so a sign-in or sign-out
 * re-renders here without a page reload.
 *
 * A signed-in account outside the app-access group gets `Unauthorized` instead
 * of the route table. See `VITE_ARDASHBOARD_ACCESS_GATE_ENABLED` in
 * utils/roles.ts.
 */
const App = () => {
  const { status, error, hasAppAccessGroup, login } = useAuth();

  /**
   * Redirects to B2C the instant the user is unauthenticated — no "Sign in"
   * button to click, matching AdminPortal (whose `MsalAuthenticationTemplate`
   * does the same automatically). `login()` navigates the whole page away, so
   * the ref just needs to stop a second call going out before that happens —
   * React 18 StrictMode double-invokes effects in dev, and MSAL rejects an
   * interaction started while one is already in flight.
   */
  const hasRequestedLogin = useRef(false);
  useEffect(() => {
    if (status === 'signedOut' && !hasRequestedLogin.current) {
      hasRequestedLogin.current = true;
      login();
    }
  }, [status, login]);

  if (status !== 'signedIn') {
    return <Login onRetry={login} errorMessage={error} />;
  }

  if (!hasAppAccessGroup) {
    return <Unauthorized />;
  }

  return (
    <Routes>
      <Route
        path={ALL_ROUTES.LOGIN}
        element={<Navigate to={ALL_ROUTES.LANDING_PAGE} replace />}
      />
      <Route
        path={ALL_ROUTES.LANDING_PAGE}
        element={
          <Layout>
            <LandingPage />
          </Layout>
        }
      />
      <Route
        path={ALL_ROUTES.HEALTH_SYSTEMWISE_SUMMARY_PAGE}
        element={
          <Layout>
            <HealthSystemwiseSummary />
          </Layout>
        }
      />
      <Route
        path={ALL_ROUTES.DASHBOARD}
        element={
          <Layout>
            <Dashboard />
          </Layout>
        }
      />
      <Route path='*' element={<NotFoundPage />} />
    </Routes>
  );
};

export default App;
